import { parseISO } from 'date-fns';

/**
 * All server timestamps are UTC. We render them in the CURRENT USER'S chosen
 * timezone + date format (set in Profile & Settings), defaulting to UTC +
 * ISO `YYYY-MM-DD` for anyone who hasn't customized — so an audit timestamp is
 * unambiguous and per-user isolated (your view vs. a teammate's view).
 *
 * Conversion + formatting is 100% native `Intl.DateTimeFormat` (no library):
 * it handles all 400+ IANA zones and DST automatically. The chosen date format
 * maps to a locale that produces its order/separators; 12h vs 24h is inferred
 * from that format's region. A short timezone label ("UTC", "EDT") is always
 * appended to times so they read unambiguously.
 */

const DEFAULT_TIME_ZONE = 'UTC';
const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';
const DEFAULT_TIME_FORMAT = '24h';

// Curated date-format menu → the (locale, date-part options) that reproduces
// each. The locale sets the date ORDER + separators only; 12h vs 24h is a
// SEPARATE explicit user choice (time_format), no longer inferred from this.
type FormatSpec = {
  locale: string;
  date: Intl.DateTimeFormatOptions;
};

const NUMERIC_DATE: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

const DATE_FORMATS: Record<string, FormatSpec> = {
  'YYYY-MM-DD': { locale: 'en-CA', date: NUMERIC_DATE },
  'MM/DD/YYYY': { locale: 'en-US', date: NUMERIC_DATE },
  'DD/MM/YYYY': { locale: 'en-GB', date: NUMERIC_DATE },
  'DD.MM.YYYY': { locale: 'de-DE', date: NUMERIC_DATE },
  'MMM D, YYYY': { locale: 'en-US', date: { year: 'numeric', month: 'short', day: 'numeric' } },
};

/** The date-format keys the Profile & Settings picker offers. */
export const DATE_FORMAT_OPTIONS = Object.keys(DATE_FORMATS);

/** The explicit clock-style choices for the picker. */
export const TIME_FORMAT_OPTIONS: { value: string; label: string }[] = [
  { value: '24h', label: '24-hour (14:18)' },
  { value: '12h', label: '12-hour (2:18 PM)' },
];

// ── module-level "active prefs" — synced once from the signed-in user (useMe)
// so the 60+ callsites stay unchanged and stay pure. Defaults keep the app on
// UTC + ISO + 24h until a user overrides their own view.
let activeTimeZone = DEFAULT_TIME_ZONE;
let activeDateFormat = DEFAULT_DATE_FORMAT;
let activeTimeFormat = DEFAULT_TIME_FORMAT;

/**
 * Point the formatters at the current user's prefs. Called from the layout
 * (Header, which reads `useMe`) and on a successful profile save. Falsy or
 * unknown values fall back to the UTC / ISO / 24h defaults.
 */
export function setDatePrefs(
  timeZone?: string | null,
  dateFormat?: string | null,
  timeFormat?: string | null,
): void {
  activeTimeZone = timeZone || DEFAULT_TIME_ZONE;
  activeDateFormat = dateFormat && dateFormat in DATE_FORMATS ? dateFormat : DEFAULT_DATE_FORMAT;
  activeTimeFormat = timeFormat === '12h' || timeFormat === '24h' ? timeFormat : DEFAULT_TIME_FORMAT;
}

function activeSpec(): FormatSpec {
  return DATE_FORMATS[activeDateFormat] ?? DATE_FORMATS[DEFAULT_DATE_FORMAT]!;
}

/**
 * Parse a server-emitted datetime string. The FastAPI backend serializes naive
 * UTC datetimes without a timezone suffix (e.g. `2026-05-24T18:18:32`); we append
 * `Z` when none is present so parsing always yields the correct UTC instant.
 * Returns `null` for null/undefined/empty input or unparseable strings.
 */
export function parseServerDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const tagged = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z';
  const d = parseISO(tagged);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Date + time in the user's timezone/format, e.g. "2026-07-08 · 14:18 UTC" or
 * "07/08/2026 · 10:18 AM EDT". Falls back to the raw string if unparseable.
 */
export function formatServerDateShort(iso: string | null | undefined): string {
  const d = parseServerDate(iso);
  if (!d) return iso ?? '—';
  const spec = activeSpec();
  const datePart = new Intl.DateTimeFormat(spec.locale, {
    timeZone: activeTimeZone,
    ...spec.date,
  }).format(d);
  // Force en-US for the TIME so the zone label reads as a friendly abbreviation
  // ("PDT", "EST", "UTC") — non-US English locales (e.g. the en-CA that ISO maps
  // to) render US zones as "GMT-7" instead. en-US still falls back to the GMT
  // offset for zones that lack a common abbreviation. 12/24h stays per the format.
  const timePart = new Intl.DateTimeFormat('en-US', {
    timeZone: activeTimeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: activeTimeFormat === '12h',
    timeZoneName: 'short',
  }).format(d);
  return `${datePart} · ${timePart}`;
}

/**
 * A timestamp for a conversation: "12:16 PM" for today, "Yesterday 12:16 PM", "Sep 8, 12:16 PM"
 * for this year, and the full date beyond it. The zone label is dropped — in a thread the full
 * "09/09/2026 · 12:16 PM PDT" on every message crowds out the message itself, and the precise
 * value stays available as the element's tooltip (see `formatServerDateShort`).
 */
export function formatThreadTime(iso: string | null | undefined): string {
  const d = parseServerDate(iso);
  if (!d) return iso ?? '—';
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone: activeTimeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: activeTimeFormat === '12h',
  }).format(d);
  const dayKey = (x: Date) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: activeTimeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(x);
  const now = new Date();
  const yesterday = new Date(now.getTime() - 86_400_000);
  if (dayKey(d) === dayKey(now)) return time;
  if (dayKey(d) === dayKey(yesterday)) return `Yesterday ${time}`;
  // (a feed that groups by day passes only same-day values here; the rest keep their date)
  const sameYear = dayKey(d).slice(0, 4) === dayKey(now).slice(0, 4);
  const day = new Intl.DateTimeFormat('en-US', {
    timeZone: activeTimeZone,
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(d);
  return `${day}, ${time}`;
}

/** The calendar day a timestamp falls on, in the user's zone — for grouping a feed by day. */
export function serverDayKey(iso: string | null | undefined): string | null {
  const d = parseServerDate(iso);
  if (!d) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: activeTimeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}

/** "Today" / "Yesterday" / "Sep 8" / "Sep 8, 2019" — the label on a feed's day separator, so a
 *  thread spanning days says which day without repeating the date on every message. */
export function formatDayLabel(iso: string | null | undefined): string {
  const key = serverDayKey(iso);
  if (!key) return '';
  const now = new Date();
  if (key === serverDayKey(now.toISOString())) return 'Today';
  if (key === serverDayKey(new Date(now.getTime() - 86_400_000).toISOString())) return 'Yesterday';
  const d = parseServerDate(iso)!;
  const sameYear = key.slice(0, 4) === serverDayKey(now.toISOString())!.slice(0, 4);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: activeTimeZone, month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }),
  }).format(d);
}

/**
 * Date only, no time, in the user's format: "2026-07-08" / "07/08/2026" /
 * "Jul 8, 2026". Falls back to the raw string if unparseable.
 */
export function formatServerDateOnly(iso: string | null | undefined): string {
  const d = parseServerDate(iso);
  if (!d) return iso ?? '—';
  const spec = activeSpec();
  return new Intl.DateTimeFormat(spec.locale, {
    timeZone: activeTimeZone,
    ...spec.date,
  }).format(d);
}

/**
 * Month + year in the user's timezone, e.g. "July 2026" — for the profile
 * "Member since" line. Uses the active timezone (NOT the browser's) so a
 * boundary instant resolves to the right month for every user; the month name
 * stays English (app UI language), independent of the numeric date_format.
 * Returns `null` for null/empty/unparseable input so the caller hides the line.
 */
export function formatServerMonthYear(iso: string | null | undefined): string | null {
  const d = parseServerDate(iso);
  if (!d) return null;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: activeTimeZone,
    month: 'long',
    year: 'numeric',
  }).format(d);
}

/**
 * A whole-seconds duration as a stopwatch label: `m:ss`, or `h:mm:ss` once it
 * passes an hour (minutes zero-padded only when hours are shown). Single source
 * of truth for BOTH the live "Elapsed:" counter and the completed-run duration,
 * so the two never drift in format. e.g. 314 → "5:14", 42 → "0:42", 3675 → "1:01:15".
 */
export function formatDuration(totalSeconds: number): string {
  const t = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}
